import { useState, useCallback, useRef, useEffect } from 'react';
import debounce from 'lodash/debounce';

/**
 * Custom hook to track Google Map viewport (bounds) and zoom level
 *
 * @param {google.maps.Map} map - Google Maps instance
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 500ms)
 * @returns {Object} { viewport, zoom, updateViewport }
 */
export const useMapViewport = (map, debounceMs = 500) => {
  const [viewport, setViewport] = useState(null);
  const [zoom, setZoom] = useState(null);
  const isInitialLoad = useRef(true);

  /**
   * Get current viewport bounds from map
   * Returns bounds in format: { neLat, neLng, swLat, swLng }
   */
  const getCurrentViewport = useCallback(() => {
    if (!map) return null;

    const bounds = map.getBounds();
    if (!bounds) return null;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    return {
      neLat: ne.lat(),
      neLng: ne.lng(),
      swLat: sw.lat(),
      swLng: sw.lng(),
    };
  }, [map]);

  /**
   * Get current zoom level from map
   */
  const getCurrentZoom = useCallback(() => {
    if (!map) return null;
    return map.getZoom();
  }, [map]);

  /**
   * Update viewport and zoom state
   * Debounced to avoid excessive updates during pan/zoom
   */
  const updateViewport = useCallback(
    debounce(() => {
      const newViewport = getCurrentViewport();
      const newZoom = getCurrentZoom();

      if (newViewport && newZoom !== null) {
        setViewport(newViewport);
        setZoom(newZoom);
      }
    }, debounceMs),
    [getCurrentViewport, getCurrentZoom, debounceMs]
  );

  /**
   * Set up map event listeners
   */
  useEffect(() => {
    if (!map) return;

    // Listen for 'idle' event - fires when map finishes panning/zooming
    const idleListener = map.addListener('idle', () => {
      updateViewport();
    });

    // Initial load - get viewport immediately
    if (isInitialLoad.current) {
      updateViewport();
      isInitialLoad.current = false;
    }

    // Cleanup
    return () => {
      if (window.google && window.google.maps && window.google.maps.event) {
        window.google.maps.event.removeListener(idleListener);
      }
      updateViewport.cancel();
    };
  }, [map, updateViewport]);

  return { viewport, zoom, updateViewport };
};

export default useMapViewport;
